import type { ArtistPhotoDto } from '@customTypes/types';
import { ArtistPhotoTag } from '@customTypes/types';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useArtistProfilePhotoListQuery,
  useDeletePhotoItemMutation,
  useUpdatePhotoItemMutation,
  useUploadPhotoMutation
} from '@/shared/api/requests';

type ArtistPhotoForm = {
  portrait: ArtistPhotoDto & { photoId: string | null };
  makeup: ArtistPhotoDto & { photoId: string | null };
  freePose: ArtistPhotoDto & { photoId: string | null };
  fullBody: ArtistPhotoDto & { photoId: string | null };
  lingerie: ArtistPhotoDto & { photoId: string | null };
};

export type ArtistPhotoFormKey = keyof ArtistPhotoForm;

export const useArtistPhoto = () => {
  const { formatMessage } = useIntl();
  const { id: profileId } = useParams({ from: '/artist/$id' });
  const search = useSearch({ from: '/artist/$id/_onboard/onboard/$step' });
  const queryClient = useQueryClient();
  const router = useRouter();
  const [IsProfile, setIsProfile] = useState('');
  const navigate = useNavigate({ from: '/artist/$id/onboard/$step' });

  const uploadMutation = useUploadPhotoMutation({});

  const updatePhotoMutation = useUpdatePhotoItemMutation();
  const deletePhotoMutation = useDeletePhotoItemMutation();

  const profilePhotosList = useArtistProfilePhotoListQuery({ profileId });

  const queries = [profilePhotosList];

  const isLoading = queries.some((q) => q.isLoading);

  const form = useForm<ArtistPhotoForm>({
    initialValues: {
      portrait: {
        photoId: null,
        file: null,
        title: 'Лицо без макияжа',
        description: formatMessage({
          id: 'pages.onboarding.artist.photo.FaceNoMakeup'
        }),
        tag: ArtistPhotoTag.FaceNoMakeup,
        ArtistProfileId: profileId,
        IsProfile: false
      },
      makeup: {
        photoId: null,
        file: null,
        title: 'Лицо / Мейкап',
        description: formatMessage({
          id: 'pages.onboarding.artist.photo.FaceWithMakeup'
        }),
        tag: ArtistPhotoTag.FaceWithMakeup,
        ArtistProfileId: profileId,
        IsProfile: false
      },
      freePose: {
        photoId: null,
        file: null,
        title: 'Фото модели в свободной позе',
        description: formatMessage({
          id: 'pages.onboarding.artist.photo.FreePose'
        }),
        tag: ArtistPhotoTag.FreePose,
        ArtistProfileId: profileId,
        IsProfile: false
      },
      fullBody: {
        photoId: null,
        file: null,
        title: 'Фото модели во весь рост на каблуках',
        description: formatMessage({
          id: 'pages.onboarding.artist.photo.FullBodyOnHeels'
        }),
        tag: ArtistPhotoTag.FullBodyOnHeels,
        ArtistProfileId: profileId,
        IsProfile: false
      },
      lingerie: {
        photoId: null,
        file: null,
        title: 'Фото модели в купальнике',
        description: formatMessage({
          id: 'pages.onboarding.artist.photo.LingerieOrSwimsuit'
        }),
        tag: ArtistPhotoTag.LingerieOrSwimsuit,
        ArtistProfileId: profileId,
        IsProfile: false
      }
    }
  });

  useEffect(() => {
    if (profilePhotosList.isSuccess && profilePhotosList.data?.data.length) {
      Object.entries(form.getValues()).forEach(([key, value]) => {
        const currentItem = profilePhotosList.data.data.filter(
          (item) => item.tag === value.tag
        );
        if (currentItem.length) {
          form.setFieldValue(`${key}.file`, currentItem[0].filePath);
          form.setFieldValue(`${key}.IsProfile`, currentItem[0].isProfile);
          form.setFieldValue(`${key}.photoId`, currentItem[0].id);
          if (currentItem[0].isProfile) setIsProfile(currentItem[0].tag!);
        }
      });
    }
  }, [profilePhotosList.data?.data]);

  const next = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    navigate({
      to: '/artist/$id/onboard/$step',
      params: {
        step: 'video'
      }
    });
  };

  const isSelectedProfilePhoto =
    form.getValues().lingerie.IsProfile ||
    form.getValues().portrait.IsProfile ||
    form.getValues().fullBody.IsProfile ||
    form.getValues().makeup.IsProfile ||
    form.getValues().freePose.IsProfile;

  const isValidForm =
    !!form.getValues().lingerie.file &&
    !!form.getValues().portrait.file &&
    !!form.getValues().fullBody.file &&
    !!form.getValues().makeup.file &&
    !!form.getValues().freePose.file;

  const onSubmit = form.onSubmit(async (values) => {
    form.setSubmitting(true);
    try {
      const promises = Object.entries(values).map(async ([key, value]) => {
        const currentImage = profilePhotosList.data?.data.filter(
          (item) => item.tag === value.tag
        );

        if (
          typeof value.file === 'string' &&
          value.IsProfile &&
          currentImage?.length
        ) {
          await updatePhotoMutation.mutateAsync({
            params: {
              photoId: currentImage[0].id.toString(),
              data: { isProfile: true }
            }
          });
        }

        if (
          typeof value.file === 'string' &&
          !value.IsProfile &&
          currentImage?.length
        ) {
          await updatePhotoMutation.mutateAsync({
            params: {
              photoId: currentImage[0].id.toString(),
              data: { isProfile: false }
            }
          });
        }

        if (typeof value.file === 'string') {
          return;
        }

        const formData = new FormData();
        formData.append(`File`, value.file!);
        formData.append(`Title`, value.title);
        formData.append(`Description`, value.description);
        formData.append(`Tag`, value.tag);
        formData.append(`ArtistProfileId`, value.ArtistProfileId);
        formData.append(`IsProfile`, JSON.stringify(value.IsProfile));

        await uploadMutation.mutateAsync(
          {
            params: {
              formData
            }
          },
          {
            onSuccess: async () => {
              notifications.show({
                title: formatMessage({ id: 'notifications.successDataUpdate' }),
                ...notificationStyleConfig
              });

              if (currentImage?.length) {
                await deletePhotoMutation.mutateAsync({
                  params: {
                    photoId: currentImage[0].id.toString()
                  }
                });
              }
            },
            onError: () => {
              form.setFieldValue(`${key}.file`, null);
              notifications.show({
                title: formatMessage({ id: 'notifications.errorOnDataUpdate' }),
                ...notificationStyleConfig
              });
            }
          }
        );
      });

      await Promise.all(promises);

      next();
    } finally {
      form.setSubmitting(false);
      await queryClient.invalidateQueries({
        queryKey: ['ArtistProfilePhotoList', profileId]
      });
    }
  });

  const onSelectProfilePhoto = (value: string) => {
    setIsProfile(value);

    Object.entries(form.getValues()).forEach(([key]) => {
      const currentField = form.getValues()[key as ArtistPhotoFormKey];
      if ((value as ArtistPhotoTag) === currentField.tag) {
        return form.setFieldValue(`${key}.IsProfile`, true);
      }
      form.setFieldValue(`${key}.IsProfile`, false);
    });
  };

  const onDeletePhoto = async (
    photoId: string | null,
    tag: ArtistPhotoFormKey
  ) => {
    if (photoId) {
      await deletePhotoMutation.mutateAsync(
        {
          params: {
            photoId
          }
        },
        {
          onSuccess: () => {
            form.setFieldValue(`${tag}.file`, null);
            form.setFieldValue(`${tag}.photoId`, null);
            form.setFieldValue(`${tag}.IsProfile`, false);

            queryClient.invalidateQueries({
              queryKey: ['ArtistProfilePhotoList', profileId]
            });
          }
        }
      );
    }
  };

  return {
    state: {
      isLoading,
      form,
      isValidForm,
      isSelectedProfilePhoto,
      IsProfile,
      profilePhotosList
    },
    functions: {
      next,
      onSubmit,
      onSelectProfilePhoto,
      setIsProfile,
      onDeletePhoto
    }
  };
};
